import { useEffect, useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { useExamples } from "../../hooks/useExamples";
import { classNames, formatJSON, uuid } from "../../lib/format";
import { JSONEditor } from "../JSONEditor";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { ExampleMeta } from "../../types/routecore";

// Browser de exemplos JSON. Ações possíveis:
//
//   - Visualizar o JSON formatado (read-only)
//   - Carregar no editor de Criar Job (transita via AppState)
//   - Disparar como POST /jobs imediatamente (atalho)
//   - Validar (POST /validate) imediatamente
type Props = {
  onNavigate: (tabId: string) => void;
};

export function TabExamples({ onNavigate }: Props) {
  const { examples, loading, error } = useExamples();
  const [selected, setSelected] = useState<ExampleMeta | null>(null);
  const [content, setContent] = useState<string>("");
  const [contentLoading, setContentLoading] = useState(false);
  const [result, setResult] = useState<APIResult<unknown> | null>(null);
  const [running, setRunning] = useState<"none" | "create" | "validate">("none");
  const { setLastPayload, setTrackedJobId, pushJobId } = useAppState();

  useEffect(() => {
    if (!selected && examples.length > 0) {
      setSelected(examples[0]);
    }
  }, [examples, selected]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setContentLoading(true);
    setContent("");
    setResult(null);
    api.getExample(selected.name).then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setContent(formatJSON(r.data ?? r.raw));
      } else {
        setContent(r.raw);
      }
      setContentLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  async function dispatchCreate() {
    if (!content.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(content);
    } catch (err) {
      setResult({
        ok: false,
        status: 0,
        data: { error: "json_parse_error", message: String(err) },
        raw: String(err),
        latencyMs: 0,
      });
      return;
    }
    setRunning("create");
    setResult(null);
    const idem = uuid();
    const r = await api.createJob(parsed, idem);
    setResult(r);
    setRunning("none");
    const data = r.data as { job_id?: string } | null;
    if (r.ok && data?.job_id) {
      setTrackedJobId(data.job_id);
      pushJobId(data.job_id);
    }
  }

  async function dispatchValidate() {
    if (!content.trim()) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(content);
    } catch (err) {
      setResult({
        ok: false,
        status: 0,
        data: { error: "json_parse_error", message: String(err) },
        raw: String(err),
        latencyMs: 0,
      });
      return;
    }
    setRunning("validate");
    setResult(null);
    const r = await api.validate(parsed);
    setResult(r);
    setRunning("none");
  }

  function sendToEditor(target: "create" | "validate") {
    setLastPayload(content);
    onNavigate(target === "create" ? "create-job" : "validate");
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Exemplos prontos"
        subtitle="Catálogo de JSONs em ../examples — cada um exercita um caminho diferente da API."
      />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <SectionCard title="Catálogo" description="Arquivos em ../examples">
          {loading && <p className="text-sm text-ink-500">carregando lista...</p>}
          {error && <p className="text-sm text-rose-600">{error}</p>}
          {!loading && !error && (
            <ul className="space-y-1">
              {examples.map((ex) => {
                const active = selected?.name === ex.name;
                return (
                  <li key={ex.name}>
                    <button
                      type="button"
                      onClick={() => setSelected(ex)}
                      className={classNames(
                        "w-full rounded-md border px-3 py-2 text-left text-sm transition",
                        active
                          ? "border-brand-500 bg-brand-50 text-brand-900"
                          : "border-ink-200 bg-white text-ink-800 hover:border-ink-300 hover:bg-ink-100",
                      )}
                    >
                      <p className="font-semibold">{ex.title}</p>
                      <p className="text-[11px] text-ink-600">{ex.summary || ex.name}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <div className="space-y-4">
          <SectionCard
            title={selected ? selected.title : "Selecione um exemplo"}
            description={selected?.summary}
            actions={
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  className="ks-btn-secondary text-xs"
                  onClick={() => sendToEditor("validate")}
                  disabled={!content}
                >
                  abrir em Validar
                </button>
                <button
                  type="button"
                  className="ks-btn-secondary text-xs"
                  onClick={() => sendToEditor("create")}
                  disabled={!content}
                >
                  abrir em Criar Job
                </button>
                <button
                  type="button"
                  className="ks-btn-primary text-xs"
                  onClick={dispatchValidate}
                  disabled={!content || running !== "none"}
                >
                  {running === "validate" ? "validando..." : "Validar agora"}
                </button>
                <button
                  type="button"
                  className="ks-btn-primary text-xs"
                  onClick={dispatchCreate}
                  disabled={!content || running !== "none"}
                >
                  {running === "create" ? "enviando..." : "Enviar como job"}
                </button>
              </div>
            }
          >
            {contentLoading && <p className="text-sm text-ink-500">carregando JSON...</p>}
            {!contentLoading && (
              <JSONEditor value={content} onChange={setContent} rows={20} />
            )}
          </SectionCard>

          <ResponseCard
            result={result}
            loading={running !== "none"}
            emptyHint="Use 'Validar agora' ou 'Enviar como job' acima para ver a resposta aqui."
          />
        </div>
      </div>
    </div>
  );
}
